/**
 * Permission predicates — the single source of truth for "can role X
 * do action Y" decisions across the app.
 *
 * Always route role-based decisions through these helpers rather than
 * inline `role === 'admin'` checks. That way:
 *   - The role-naming refactor (staff → user) is invisible to callers;
 *     they just call canExportData() and the helper handles both.
 *   - Adding the Power User tier or any future role only touches this
 *     file plus the predicate that needs to widen.
 *   - It's grep-friendly: searching for "canSeeFinancials" finds every
 *     surface where money is gated, instead of trying to find every
 *     `role === 'admin'` and figuring out which ones are about money.
 *
 * Predicates take the raw `role` from useAuth() (which may include the
 * legacy 'staff' alias). They normalize internally so callers don't
 * have to.
 */

import type { UserRole } from '../types';
import { normalizeRole } from './role';

/** Admin portal access — both admin and Power User. */
export const canAccessAdmin = (role: UserRole | null | undefined): boolean => {
  const r = normalizeRole(role);
  return r === 'admin' || r === 'power';
};

/** User management (rename, password reset, lock, role change). Admin only. */
export const canManageUsers = (role: UserRole | null | undefined): boolean =>
  normalizeRole(role) === 'admin';

/** See money fields — Distillery Cost, revenue/profit tiles, money
 *  columns in reports. Admin only. (Barrel purchase cost is gated
 *  separately by canSeeBarrelCost, which also allows Power Users.) */
export const canSeeFinancials = (role: UserRole | null | undefined): boolean =>
  normalizeRole(role) === 'admin';

/** See/enter Barrel Cost (the purchase price of a barrel). Admin AND
 *  Power User — acquisition cost is operational for power users, distinct
 *  from revenue/profit which stays admin-only (canSeeFinancials). Stored
 *  in the gated barrel_pricing collection, never on the barrel doc. */
export const canSeeBarrelCost = (role: UserRole | null | undefined): boolean => {
  const r = normalizeRole(role);
  return r === 'admin' || r === 'power';
};

/** Rename a barrel (change its number). Admin AND Power User. The
 *  `renameBarrel` Cloud Function enforces the same rule server-side;
 *  this only decides whether the "Rename this barrel…" control shows. */
export const canRenameBarrel = (role: UserRole | null | undefined): boolean => {
  const r = normalizeRole(role);
  return r === 'admin' || r === 'power';
};

/** CSV exports across the app — admin and Power User can export
 *  filtered lists; regular Users cannot. */
export const canExportData = (role: UserRole | null | undefined): boolean => {
  const r = normalizeRole(role);
  return r === 'admin' || r === 'power';
};

/** Firestore backup + restore tooling. Admin only. */
export const canSeeBackups = (role: UserRole | null | undefined): boolean =>
  normalizeRole(role) === 'admin';

/** Editing the per-bottle price during the Bottle It flow. Available
 *  to every signed-in user — confirmed in the RBAC overhaul spec. */
export const canEditBottlePrice = (_role: UserRole | null | undefined): boolean =>
  true;

/** Soft-archive a barrel (admin-only — staff/users/power can't surface
 *  archived barrels and can't write the archive flag). Phase 1 leaves
 *  this admin-only; revisit if Power Users ever need it. */
export const canArchiveBarrels = (role: UserRole | null | undefined): boolean =>
  normalizeRole(role) === 'admin';

/* -------------------------------------------------------------------------- */
/* Projects                                                                   */
/* -------------------------------------------------------------------------- */

/** Edit a project — name, recipient, priority, status, goals,
 *  packaging, yield, notes, links, tasting notes. Open to staff+ so
 *  the team can collaboratively scope work; admin-only fields
 *  (rank / archive) are gated separately and at the rules layer. */
export const canEditProjects = (role: UserRole | null | undefined): boolean => {
  const r = normalizeRole(role);
  return r === 'admin' || r === 'power' || r === 'user';
};

/** Admin power-ranking of projects (drag-and-drop reorder).
 *  Admin only — the rank is the executive view of "what matters most
 *  right now" and gets surfaced as a #1/#2/#3 badge on every card. */
export const canRankProjects = (role: UserRole | null | undefined): boolean =>
  normalizeRole(role) === 'admin';

/** Soft-archive (and unarchive) a project. Admin only — hides the
 *  project from default views without losing its tasting-note thread
 *  or linked-item history. */
export const canArchiveProjects = (role: UserRole | null | undefined): boolean =>
  normalizeRole(role) === 'admin';

/* -------------------------------------------------------------------------- */
/* Barrel templates                                                           */
/* -------------------------------------------------------------------------- */

/** Create a new barrel template. Any signed-in role. */
export const canCreateTemplates = (
  role: UserRole | null | undefined,
): boolean => normalizeRole(role) !== null;

/**
 * Edit a specific template — owner or admin. Runtime check needs
 * both role AND the template's `createdBy` uid; pass the current
 * user's uid + the template's owner uid in.
 */
export const canEditTemplate = (
  role: UserRole | null | undefined,
  currentUid: string | null | undefined,
  templateOwnerUid: string | null | undefined,
): boolean => {
  if (normalizeRole(role) === 'admin') return true;
  return Boolean(currentUid && templateOwnerUid && currentUid === templateOwnerUid);
};

/** Same logic as edit (rules layer treats them identically). */
export const canDeleteTemplate = canEditTemplate;

/* -------------------------------------------------------------------------- */
/* Volume draws                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Undo a volume draw — the person who LOGGED it or an admin. Same
 * owner-or-admin shape as {@link canEditTemplate}; pass the current
 * user's uid + the draw's `drawBy` uid.
 *
 * This gates the Undo control in the UI. The real enforcement is the
 * `undoVolumeDraw` Cloud Function (volume_draws are append-only at the
 * rules layer, so the reversal has to run server-side with the Admin
 * SDK, which re-checks this same condition).
 */
export const canUndoDraw = (
  role: UserRole | null | undefined,
  currentUid: string | null | undefined,
  drawByUid: string | null | undefined,
): boolean => {
  if (normalizeRole(role) === 'admin') return true;
  return Boolean(currentUid && drawByUid && currentUid === drawByUid);
};

/* -------------------------------------------------------------------------- */
/* Bottling                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Anyone signed in can bottle a barrel. (Previously this was a two-step
 * staff-submit + admin-confirm flow; the admin-review requirement was
 * dropped in favor of an inline confirm-dump-date step in the modal.)
 */
export const canBottleBarrel = (
  role: UserRole | null | undefined,
): boolean => normalizeRole(role) !== null;
